import React, {FC, useState} from 'react';
import {connect} from 'react-redux';

import {sanitizeTopicLinkUrl} from '../../../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {EventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PopoverTransferProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {openInBrowser} from '../../../../../../../srcCommon/browser/openInBrowser';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {ModifiersEnum} from '../../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {buildTopicPublicId} from '../../../../../../helpers/modelHelpers';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {InboxesSearchLocationsEnum} from '../../../../../../models/url/models/inboxesUrlModel';
import {getCompanyHost} from '../../../../../../store/data/company/companySelectors';
import {searchByParametersAction} from '../../../../../../store/stateless/searchActions';
import {AddOrEditTopicDialogContainer} from '../topicsAndTags/addOrEditTopicDialogContainer';
import {TopicDropdown} from './topicDropdown';

/*
 * Props.
 */

interface TopicDropdownContainerProps extends Omit<PopoverTransferProps, 'wrap'> {
  topic: TopicModel;
  createAndAddTopic?: (link: string, name: string) => void;
  removeTopic?: () => void;
  isApplicationTopic?: boolean;
  onToggleSummary?: () => void;
  onOpenUrl?: () => void;
  shouldShowViewDetails?: boolean;
}

interface TopicDropdownContainerComponentProps extends TopicDropdownContainerProps {
  searchByTopic: () => void;
  host: string;
}

/*
 * Component.
 */

export const TopicDropdownContainerComponent: FC<TopicDropdownContainerComponentProps> = (props) => {
  const {topic, isApplicationTopic, onToggleSummary, onOpenUrl} = props;
  const [isEditTopicOpen, setIsEditTopicOpen] = useState(false);

  if (isEditTopicOpen) {
    const onRequestClose = () => {
      props.onRequestClose(); // Close the dropdown

      setIsEditTopicOpen(false); // Switch back to List
    };

    return <AddOrEditTopicDialogContainer {...props} onRequestClose={onRequestClose} topicToEdit={topic} />;
  }

  const openTopicLink = () => {
    openInBrowser(sanitizeTopicLinkUrl(topic.externalLink));

    if (onOpenUrl) {
      onOpenUrl();
    }
  };

  const onEditTopicSelect: EventHandler = (e) => {
    e.preventDefault(); // Keep the dropdown open.
    setIsEditTopicOpen(true); // Switch to Edit Link.
  };

  return (
    <TopicDropdown
      {...props}
      openTopicLink={openTopicLink}
      onEditTopicSelect={onEditTopicSelect}
      isApplicationTopic={isApplicationTopic}
      onToggleSummary={onToggleSummary}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TopicDropdownContainerProps>()(() => ({
  host: getCompanyHost,
}));

const mapDispatchToProps = makeMapDispatchToProps<TopicDropdownContainerProps>()(({topic}) => ({
  searchByTopic: () =>
    searchByParametersAction({
      location: InboxesSearchLocationsEnum.GLOBAL,
      query: `${ModifiersEnum.OBJECT}:${buildTopicPublicId(topic)}`,
    }),
}));

export const TopicDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TopicDropdownContainerComponent);
